import { inject, Injectable } from '@angular/core';

import { Client } from 'src/providers/client/client';
import { PosthogService } from 'src/shared/services/posthog.service';
import { AuthModalService } from 'src/shared/services/auth-modal.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { SubscriptionGateService } from 'src/shared/services/subscription-gate.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { LoadingStateService } from 'src/tidy-ui-components/providers/loading-state/loading-state.service';

import { SelectCategoryPage } from 'src/pages/select-category/select-category';
import { BookJobPage } from 'src/pages/booking/book-job/book-job';
import { BookPage } from 'src/pages/book/book';
import { PlansPage } from 'src/pages/plans/plans';
import { SelectResidentialBookingTestPage } from 'src/pages/select-residential-booking-test/select-residential-booking-test';

import { FEATURE_FLAGS, FEATURE_FLAG_DEFAULTS } from 'src/shared/constants/feature-flags';

import { environment } from 'src/environments/environment';

const PLANS_BOOKING_TYPES = ['reschedule_plan', 'add_one_time_job', 'reschedule_job'];

@Injectable({
  providedIn: 'root'
})
export class BookService {

  private authModalService = inject(AuthModalService);
  private demoBlock = inject(DemoBlockService);
  private subscriptionGate = inject(SubscriptionGateService);
  private client = inject(Client);
  private loadingState = inject(LoadingStateService);
  private posthogService = inject(PosthogService);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);

  static getBookingViewV2FlagKey(): string {
    return environment.environment === 'production'
      ? FEATURE_FLAGS.RESIDENTIAL_BOOKING_VIEW_V2
      : FEATURE_FLAGS.RESIDENTIAL_BOOKING_VIEW_V2_SANDBOX;
  }

  // Call right after posthogService.identify() + reloadFeatureFlags() in every
  // auth path (app boot for already-authenticated users, sign-in, signup,
  // guest-type pick). Stores the booking-view V2 variant in localStorage so
  // resolveBookingViewV2Variant() can return synchronously when the booking
  // view opens — no PostHog round-trip at Book Job click time.
  // Safe to call for any account type; non-residential users (rental/pro) are
  // not in the flag's targeting and will get null, which is not cached.
  async cacheBookingViewV2Variant(): Promise<void> {
    const flagKey = BookService.getBookingViewV2FlagKey();
    const customerId = localStorage.getItem('customer_id') || localStorage.getItem('customerId') || 'unknown';
    const email = localStorage.getItem('customer_email') || localStorage.getItem('email') || localStorage.getItem('clientEmail') || 'unknown';
    try {
      await this.posthogService.waitForFlags();
      const variant = await this.posthogService.getFeatureFlag(flagKey);
      if (variant && typeof variant === 'string') {
        localStorage.setItem(`posthog_variant_${flagKey}`, variant);
        console.log(
          `[PostHog] Identified user (id=${customerId}, email=${email}) assigned variant "${variant}" for flag "${flagKey}"`
        );
      } else {
        console.log(
          `[PostHog] Identified user (id=${customerId}, email=${email}) NOT assigned a variant for flag "${flagKey}" (outside targeting or flag disabled, raw=${JSON.stringify(variant)})`
        );
      }
    } catch (err) {
      console.error('[PostHog] cacheBookingViewV2Variant failed:', err);
    }
  }

  async resolveBookingViewV2Variant(): Promise<string> {
    const flagKey = BookService.getBookingViewV2FlagKey();
    const cacheKey = `posthog_variant_${flagKey}`;

    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      console.log(`[PostHog] booking view v2: "${cached}" (cached) | flag: ${flagKey}`);
      return cached;
    }

    await this.posthogService.waitForIdentification();
    await this.posthogService.waitForFlags();
    const raw = await this.posthogService.getFeatureFlag(flagKey);
    const variant = (raw as string) || FEATURE_FLAG_DEFAULTS[flagKey] as string;
    console.log(`[PostHog] booking view v2: "${variant}" (fresh, raw=${JSON.stringify(raw)}) | flag: ${flagKey}`);

    if (raw !== null && raw !== undefined && raw !== false) {
      localStorage.setItem(cacheKey, variant);
    }

    return variant;
  }

  async goToBookJob(params: any): Promise<void> {
    // Booking writes to the shared demo account — in the demo session show the
    // "create an account" prompt instead of starting (or prompting to book).
    if (this.demoBlock.block()) {
      this.loadingState.loading = false;
      return;
    }
    // Fresh open of the booking view — clear any stale restore state from a
    // previous session so the user lands on House Cleaning by default. The
    // restore state is only meant to survive back-navigation from a sub-page
    // (request-job / confirm-booking), not a full dialog dismissal.
    try { localStorage.removeItem('book-job-chooser-restore-state'); } catch (_e) { /* noop */ }
    // Also drop any persisted pro dropdown list so BookJobPage fetches it fresh.
    // The 'pros' storage key is shared with the Messages pages (which write an
    // account-wide contact list in a different shape) and is scoped to neither
    // address nor category, so a leftover value from another context would be
    // reused by fetchPros() and the client's own Pros would vanish from the
    // dropdown. Entry points that pass params.pros explicitly are unaffected.
    await this.storage.delete('pros');
    const isGuestAccount = await this.storage.retrieve('isGuestAccount');
    if (isGuestAccount) {
      this.loadingState.loading = false;
      await this.authModalService.openAuthModal('auth', 'signup', 'Create Account to Book');
      const stillGuest = await this.storage.retrieve('isGuestAccount');
      if (stillGuest) { return; }
    }
    // New rental users must schedule a setup call before they can book a job. Anywhere in the app,
    // gate the booking and send them to the "Schedule a Call to Get Started" prompt → the
    // Meet-Your-Account-Manager page. Only fires for a real, just-created rental account that
    // hasn't scheduled yet; existing rental users pass straight through.
    if (await this.demoBlock.shouldGateNewRental()) {
      this.loadingState.loading = false;
      this.demoBlock.promptNewRental();
      return;
    }
    // Rental clients who manage their own cleaning & maintenance (no C&M add-on on
    // their subscription) can't book jobs — prompt them to upgrade instead.
    if (await this.subscriptionGate.gate()) {
      this.loadingState.loading = false;
      return;
    }
    const isRentalClient = localStorage.getItem('isRentalClient') === 'true';
    if (isRentalClient) {
      this.rightSidePanelService.navigateTo('select-category', params, SelectCategoryPage);
      return;
    }
    const skipPlansCheck = PLANS_BOOKING_TYPES.includes(params.bookingType);
    if (!skipPlansCheck && params.address) {
      const addresses = await this.client.getMoreDetailAddresses();
      const detailedAddress = addresses.find((addr: any) => addr.id === params.address.id);
      if (detailedAddress?.plans?.length > 0) {
        const addressFilter = this.client.parseAddressList(addresses, true);
        const plansParams = {
          address: detailedAddress,
          plans: detailedAddress.plans,
          addressFilter,
        };
        this.rightSidePanelService.navigateTo('plans', plansParams, PlansPage);
        return;
      }
    }
    const email = localStorage.getItem('clientEmail') || '';
    if (email.includes('test123') && !params.bookingViewV2Variant) {
      this.rightSidePanelService.navigateTo('select-residential-booking-test', params, SelectResidentialBookingTestPage);
      return;
    }
    const variant = params.bookingViewV2Variant || (await this.resolveBookingViewV2Variant());
    if (variant === 'new-view-collapsed') {
      // New view with the Service step kept visible (so the user can navigate
      // back to change category) but House Cleaning pre-selected and landing
      // directly on the Details step (1 hour / 2.5 hour / etc).
      const enrichedParams = { ...params, pageVariant: 'all-categories-select-time', preSelectHouseCleaning: true };
      this.rightSidePanelService.navigateTo('book', enrichedParams, BookPage);
    } else if (variant === 'new-view-uncollapsed') {
      // New view starting at the Service step — user picks a category first.
      // Set preSelectHouseCleaning explicitly to false so a stale `true` left in
      // the merged dialog-params by a prior 'new-view-collapsed' selection can't
      // bleed through and force the page onto the Details step.
      const enrichedParams = { ...params, pageVariant: 'all-categories-select-time', preSelectHouseCleaning: false };
      this.rightSidePanelService.navigateTo('book', enrichedParams, BookPage);
    } else if (variant === 'old-view-uncollapsed') {
      const enrichedParams = { ...params, showCategoryChooser: true, collapsableChooser: false };
      this.rightSidePanelService.navigateTo('book-job', enrichedParams, BookJobPage);
    } else if (variant === 'control') {
      const enrichedParams = { ...params, showCategoryChooser: true, collapsableChooser: true };
      this.rightSidePanelService.navigateTo('book-job', enrichedParams, BookJobPage);
    } else {
      const enrichedParams = { ...params, showCategoryChooser: false };
      this.rightSidePanelService.navigateTo('book-job', enrichedParams, BookJobPage);
    }
  }

}
